import { mapState } from 'pinia';
import stores from 'store/modules';
import { throttle, debounce } from 'lodash';
import { TYPES_INBOX } from 'store/modules/user';
import Conversation from '../store/models/Conversation';
import Contact from '../store/models/Contact';
import MessageSimple from '../store/models/MessageSimple';
import ModalOptInMixin from './modal-opt-in';

export default {
    mixins: [ModalOptInMixin],

    data: () => ({
        channel: null,
        contacts: {},
        bulkContacts: {
            checked: [],
            closing: [],
            deleting: [],
            tagging: [],
        },
        deleting: null,
        blocking: null,
        failed: false,
        applyOrRemoveTagsStatus: false,
        loading: true,
        moreLoading: false,
        pusher: null,
        showModalChooseParticipants: false,
        showModalReassign: false,
        showModalCall: false,
        showModalGroupTitle: false,
        showCancellingMessage: false,
        showCancellingMessageItem: null,

        closedAll: false,
        selectedItemMedia: false,

        integrationSlideShow: false,
        integrationSlideShowFinish: false,

        showModalEditScheduled: false,
        showModalEditScheduledItem: null,

        showModalCheckConversation: false,

        draftMethods: {},
    }),

    computed: {
        routeId() {
            return parseInt(this.$route.params.id) || 0;
        },

        inboxesIds() {
            const ids = this.inboxesArray
                .filter((item) => item.memberIds.includes(this.user.id))
                .map((item) => item.id);

            return [0, ...ids];
        },

        hasIsBlocked() {
            return this.conversation.contact.is_blocked;
        },

        hasOptOut() {
            return this.conversation.contact.opt_out === 'soft';
        },

        useConversations() {
            return Boolean(
                this.conversationsFilter !== 'scheduled' && this.conversationsFilter !== 'draft'
            );
        },

        isConversationIdEqualInboxId() {
            if (this.currentInboxId === null) return true;
            if (!this.conversation) return true;

            return this.conversation.inbox_id === this.currentInboxId;
        },

        ...mapState(stores.Inboxes, [
            'inbox',
            'inboxes',
            'showModalSelectInbox',
            'inboxesArray',
            'currentInboxId',
            'currentInboxTypeId',
            'currentInboxType',
        ]),
        ...mapState(stores.User, ['user', 'userPermissions', 'isRecordAutomatically']),
        ...mapState(stores.Organization, ['featureFlags']),
        ...mapState(stores.Tags, ['tags']),
        ...mapState(stores.Conversations, [
            'conversationsOrder',
            'conversationsPagination',
            'conversationsParameters',
            'conversationsBulkAll',
            'conversations',
            'conversationsArray',
            'conversation',
            'searchShowConversation',
            'conversationsFilter',
            'conversationsOrderFilter',
        ]),
        ...mapState(stores.Search, [
            'conversationsSearchPagination',
            'contactsSearchAllLoading',
            'searchCreateConversation',
        ]),
        ...mapState(stores.VoiceOrVideo, ['twilioAudioInput', 'twilioAudioOutInput']),
        ...mapState(stores.StarterConversationModule, ['starterShow']),
    },

    watch: {
        async searchShowConversation(value) {
            if (this.name !== 'conversations') return;

            if (!value) {
                this.$store.dispatch('searchUpdateState', {
                    selectedTypeSearch: null,
                    selectedTypeItemsSearch: [],
                    searchShowConversation: false,
                    querySearch: '',
                });

                if (!this.searchCreateConversation) {
                    this.throttleLoad();
                }

                this.$store.dispatch('searchUpdateState', {
                    searchDisabled: true,
                    searchCreateConversation: false,
                });

                await this.$delay(1000);

                this.$store.dispatch('searchUpdateState', {
                    searchDisabled: false,
                });
            } else {
                if (this.conversation) {
                    this.$router.replace(`/conversations/${this.conversation.id}`);
                }
            }
        },

        async routeId(newId) {
            if (newId) {
                this.startingCancel(newId);
            }

            this.setConversation(newId);
        },

        'conversation.id'() {
            if (this.conversation) {
                this.$store.dispatch('initConversation', this.conversation);
            }
        },
    },

    methods: {
        updateState(key, value) {
            this[key] = value;
        },

        reset() {
            this.failed = false;
            this.loading = this.useConversations;
            this.moreLoading = false;
            this.$store.dispatch('handleChangeStatus', false);

            const data = {
                item: null,
                items: {},
                order: [],
                pagination: {
                    hasMore: false,
                },
            };

            this.$store.dispatch('updateStateConversations', data);
        },

        load(callback = () => {}, teamTypeId = null, teamType = null, reset = true) {
            if (!this.inboxesArray.length || !this.useConversations) {
                this.reset();
                this.loading = false;

                return;
            }

            this.reset();

            if (this.routeId) {
                this.switchToInbox(callback);
            } else {
                this.loadConversations(callback, teamTypeId, teamType);
            }
        },

        async switchToInbox(callback) {
            try {
                const data = await this.$store.dispatch(
                    'updateConversationsByIdSwitch',
                    this.routeId
                );

                if (data?.filter && ['closed', 'outbound'].includes(data?.filter)) {
                    this.$store.dispatch('setFilterKey', data?.filter);
                }
            } catch (e) {
                this.$router.replace({
                    path: `/${this.route}/${this.$route.params.id}`,
                    query: this.$route.query,
                });

                this.$log.error(e, this.$options.__file, 'switchToInbox');
            } finally {
                this.loadConversations(callback);
            }
        },

        async loadConversations(callback, teamTypeId = null, teamType = null) {
            const id = teamTypeId || (this.currentInboxTypeId ? this.currentInboxTypeId : 0);
            const type =
                teamType || (this.currentInboxType ? this.currentInboxType : TYPES_INBOX.UNIFIED);

            let params = {
                limit: this.conversationsParameters.limit,
                filter: this.conversationsFilter,
                order: this.conversationsOrderFilter,
                team_id: id,
                team_type: type,
                exclude: this.routeId,
                sort_by:
                    this.conversationsFilter === 'outbound'
                        ? 'last_message_at'
                        : 'last_manual_message_at',
            };

            if (this.routeId) {
                try {
                    const array = [this.$store.dispatch('getConversations', { params })];

                    if (
                        !this.conversation ||
                        (this.conversation && this.conversation.id !== this.routeId)
                    ) {
                        if (!this.showModalCheckConversation) {
                            array.push(
                                this.$store.dispatch('getConversationsById', { id: this.routeId }),
                                this.$store.dispatch('getMessagesByConversationId', {
                                    conversationId: this.routeId,
                                    messageId: +this.$route.query.messageId,
                                })
                            );
                        }

                        await Promise.all(array);
                    } else {
                        if (!this.showModalCheckConversation) {
                            array.push(
                                this.$store.dispatch('getMessagesByConversationId', {
                                    conversationId: this.routeId,
                                    messageId: +this.$route.query.messageId,
                                })
                            );
                        }

                        await Promise.all(array);
                    }

                    this.$store.dispatch('updateStateMessages', {
                        itemsLoadingForConversation: false,
                    });

                    if (this.conversation === null) {
                        this.handleListResponse(true);
                    } else {
                        await this.$store.dispatch(
                            'messageReassignLogs',
                            this.conversation.reassign_logs
                        );
                        this.handleListResponse();
                        this.handleShowResponse();
                    }
                } catch (e) {
                    this.failed = true;

                    this.$log.error(e, this.$options.__file, 'loadConversations');
                } finally {
                    this.echoConnect();
                    this.loading = false;
                    callback();
                }
            } else {
                try {
                    await this.$store.dispatch('getConversations', { params });

                    this.$store.dispatch('updateStateMessages', {
                        itemsLoadingForConversation: false,
                    });

                    this.handleListResponse(true);
                } catch (e) {
                    this.failed = true;

                    this.$log.error(e, this.$options.__file, 'loadConversations');
                } finally {
                    this.echoConnect();
                    this.loading = false;
                    callback();
                }
            }
        },

        handleDraftMethods(methods) {
            this.draftMethods = methods;
        },

        async handleListResponse(switchToFirst) {
            for (let key in this.conversations) {
                const item = this.conversations[key];

                this.handleAddContact(item.contact_id, item.id);
            }

            const useRedirect = Boolean(
                this.type !== 'extension' &&
                    this.$route.name.includes('conversations') &&
                    this.$route.name !== 'conversations.empty'
            );

            if (useRedirect && switchToFirst) {
                if (this.conversationsOrder.length) {
                    this.$router.replace({
                        path: `/${this.route}/${this.conversationsOrder[0]}`,
                        query: this.$route.query,
                    });
                } else {
                    this.$router.replace({
                        path: `/${this.route}`,
                        query: this.$route.query,
                    });
                }
            }
        },

        async handleShowResponse() {
            const conversation = this.conversation;

            await this.$store.dispatch('updateOrAddConversation', { conversation });
            await this.handleAddContact(conversation.contact_id, conversation.id);
            await this.setConversation(conversation.id);
        },

        async loadMore() {
            if (this.searchShowConversation) {
                this.loadMoreSearch();
            } else {
                this.loadMoreDefault();
            }
        },

        async loadMoreSearch() {
            try {
                this.moreLoading = true;

                await this.$store.dispatch('searchConversations', {
                    isSearchTop: true,
                    page: this.conversationsSearchPagination.current_page + 1,
                    enableLoading: false,
                });
            } catch (e) {
                this.$log.error(e, this.$options.__file, 'loadMoreSearch');
            } finally {
                this.moreLoading = false;
                this.closedAll = false;
            }
        },

        async loadMoreDefault() {
            const id = this.currentInboxTypeId ? this.currentInboxTypeId : 0;
            const type = this.currentInboxType ? this.currentInboxType : TYPES_INBOX.UNIFIED;

            const params = {
                limit: this.conversationsParameters.moreLimit,
                filter: this.conversationsFilter,
                order: this.conversationsOrderFilter,
                team_id: id,
                team_type: type,
                sort_by:
                    this.conversationsFilter === 'outbound'
                        ? 'last_message_at'
                        : 'last_manual_message_at',
                offset: this.conversationsOrder.length,
            };

            this.moreLoading = true;

            try {
                const list = await this.$store.dispatch('getConversations', {
                    params,
                    isMore: true,
                });

                this.handleLoadMoreResponse(list);
            } catch (e) {
                this.$log.error(e, this.$options.__file, 'loadMoreDefault');
            } finally {
                this.moreLoading = false;
                this.closedAll = false;
            }
        },

        async handleLoadMoreResponse() {
            for (let key in this.conversations) {
                const item = this.conversations[key];

                this.handleAddContact(item.contact_id, item.id);
            }
        },

        async setConversation(id) {
            const conversation = this.conversations[parseInt(id)];

            if (conversation) {
                if (conversation.id && parseInt(id) !== conversation.id) {
                    let remove = false;

                    if (this.conversationsFilter !== 'closed' && conversation.closed_at) {
                        remove = true;
                    } else if (
                        this.conversationsFilter === 'assigned' &&
                        conversation.user_id !== this.user.id
                    ) {
                        remove = true;
                    } else if (this.conversationsFilter === 'unassigned' && conversation.user_id) {
                        remove = true;
                    }

                    if (remove) {
                        this.updateAndRemoveConversation(conversation, true);
                    }
                }
            }

            this.$store.dispatch('initConversation', conversation || null);
        },

        async startingOpen() {
            if (!this.starterShow) {
                this.$store.dispatch('initConversation', null);
                this.$store.dispatch('handleChangeStatus', true);
                this.$router.replace({
                    path: `/${this.route}`,
                    query: this.$route.query,
                });
            }
        },

        async startingCancel(id) {
            if (this.starterShow) {
                this.$store.dispatch('handleChangeStatus', false);
                this.$store.dispatch('deleteConversation', 0);

                if (!id) {
                    this.$router.replace({
                        path: `/${this.route}`,
                        query: this.$route.query,
                    });
                }
            }
        },

        async filterChanged(filter, callback = () => {}) {
            if (this.$route.params.id) {
                this.$router.push({
                    path: `/${this.route}`,
                    query: this.$route.query,
                });
            }

            this.$store.dispatch('setFilterKey', filter);

            this.throttleLoad(callback);
        },

        async orderChanged(filter, callback = () => {}) {
            if (this.$route.params.id) {
                this.$router.push({
                    path: `/${this.route}`,
                    query: this.$route.query,
                });
            }

            this.$store.dispatch('setFilterOrderKey', filter);

            this.throttleLoad(callback);
        },

        async open(id) {
            this.$router.push({
                path: `/${this.route}/${id}`,
                query: this.$route.query,
            });
        },

        async close(idOrIds, bulk) {
            if (idOrIds === 0) {
                this.$store.dispatch('handleChangeStatus', false);
                this.$store.dispatch('deleteConversation', 0);
                this.$router.replace({
                    path: `/${this.route}`,
                    query: this.$route.query,
                });

                return;
            }

            if (Array.isArray(idOrIds)) {
                const teamId = this.currentInboxId === null ? 0 : this.currentInboxId;
                const deleteAll = typeof bulk === 'object' ? bulk.all : false;
                const except = typeof bulk === 'object' ? bulk.except : [];

                this.closedAll = deleteAll;

                if (teamId) {
                    await this.$store.dispatch('updateConversationsBatchQueueClose', {
                        ids: idOrIds,
                        teamId: teamId,
                        deleteAll: deleteAll,
                        except: except,
                    });
                    await this.$store.dispatch('bulkCloseConversations', idOrIds);
                    await this.$store.dispatch('setConversationsBulkAll', false);

                    if (deleteAll) {
                        this.$router.replace('/conversations');
                    }
                } else {
                    const inboxes = {};

                    idOrIds.forEach((id) => {
                        const inboxId = this.conversations[id].inbox_id;
                        if (typeof inboxes[inboxId] === 'object') inboxes[inboxId].push(id);
                        else inboxes[inboxId] = [id];
                    });

                    for (let inboxId in inboxes) {
                        await this.$store.dispatch('updateConversationsBatchQueueClose', {
                            ids: inboxes[inboxId],
                            teamId: inboxId,
                            deleteAll: deleteAll,
                            except: except,
                        });

                        await this.$store.dispatch('bulkCloseConversations', inboxes[inboxId]);
                    }

                    await this.$store.dispatch('setConversationsBulkAll', false);

                    if (deleteAll) {
                        this.$router.replace('/conversations');
                    }
                }
            } else {
                if (this.routeId === idOrIds) {
                    this.$router.replace({
                        path: `/${this.route}`,
                        query: this.$route.query,
                    });
                }
                try {
                    const conversation = await this.$store.dispatch(
                        'updateConversationsByIdClose',
                        idOrIds
                    );
                    this.updateAndRemoveConversation(conversation);
                    this.loadIfLessThenLimit();
                } catch (e) {
                    this.$log.error(e, this.$options.__file, 'close');
                }
            }
        },

        async reopen(id, callback = () => {}) {
            try {
                const conversation = await this.$store.dispatch('updateConversationsByIdOpen', id);
                await this.updateAndRemoveConversation(conversation);
                this.loadIfLessThenLimit();
                this.$store.dispatch('showToast', {
                    type: 'success',
                    message: `Conversation with ${conversation.fullNameContacts} was reopened.`,
                });
                callback();
            } catch (e) {
                const originalID = e.response?.data?.conversation_id;

                if (originalID) {
                    if (this.type === 'extension') {
                        this.$store.dispatch('showToast', {
                            type: 'danger',
                            message: `Found another open <a href="#" onclick="this.openConversationContactView(event, ${originalID})">conversation</a>.`,
                        });
                        return;
                    }

                    this.$store.dispatch('showToast', {
                        type: 'danger',
                        message: `Found another open <a href="/conversations/${originalID}" onclick="this.openConversation(event, ${originalID})">conversation</a>.`,
                    });
                }
                callback(e);
                this.$log.error(e, this.$options.__file, 'reopen');
            }
        },

        async updateAndRemoveConversation(conversation, skipRedirect) {
            if (this.currentInboxId !== null && this.currentInboxId !== conversation.inbox_id)
                return;
            if (!(conversation instanceof Conversation)) {
                conversation = new Conversation(conversation);
            }

            if (!this.conversations[conversation.id]) return;

            await this.$store.dispatch('updateOrAddConversation', { conversation });

            if (!skipRedirect && this.conversation && this.conversation.id === conversation.id) {
                this.$router.push({
                    path: `/${this.route}`,
                    query: this.$route.query,
                });
            }

            setTimeout(async () => {
                await this.$store.dispatch('updateOrder', {
                    id: conversation.id,
                    type: 'delete',
                });

                if (Array.isArray(this.contacts[conversation.contact_id])) {
                    this.contacts[conversation.contact_id] = this.contacts[
                        conversation.contact_id
                    ].filter((id) => id !== conversation.id);

                    if (!this.contacts[conversation.contact_id].length) {
                        delete this.contacts[conversation.contact_id];
                    }
                }
            }, 1000);
        },

        async unread(ids) {
            for (const id of ids) {
                this.$store
                    .dispatch('updateConversationsByIdUnread', { id, force: true })
                    .then((conversation) => {
                        this.conversationUpdated(conversation);
                    });
            }
        },

        async conversationUpdated(e) {
            if (this.currentInboxId !== null && this.currentInboxId !== e.inbox_id) return;

            if (
                (this.conversationsFilter !== 'outbound' &&
                    (this.conversationsFilter === 'assigned' || e.started_at || e.closed_at)) ||
                (this.conversationsFilter === 'outbound' && !e.started_at && !e.closed_at)
            ) {
                if (['open'].includes(this.conversationsFilter) && e.started_at) {
                    this.updateConversation(e);
                } else if (['unread', 'outbound'].includes(this.conversationsFilter)) {
                    this.updateConversation(e);
                } else if (this.conversationsFilter === 'assigned') {
                    if (e.user_id === this.user.id && e.started_at) {
                        this.updateConversation(e);
                    } else {
                        this.updateAndRemoveConversation(e);
                    }
                } else if (this.conversationsFilter === 'unassigned') {
                    if (e.user_id === 0) {
                        this.updateConversation(e);
                    } else {
                        this.updateAndRemoveConversation(e);
                    }
                }

                this.$store.dispatch('messageReassignLogs', e.reassign_logs);
            }
        },

        async updateConversation(conversation) {
            if (!(conversation instanceof Conversation)) {
                conversation = new Conversation(conversation);
            }

            await this.$store.dispatch('updateOrAddConversation', { conversation });

            if (this.conversation && this.conversation.id === conversation.id) {
                await this.$store.dispatch('initConversation', conversation);
            }
        },

        async onApplyOrRemoveTagsStatus(ids) {
            const checked = [];

            for (const id of ids) {
                if (this.conversations[id] != null && this.conversations[id].contact != null) {
                    checked.push(this.conversations[id].contact.id);
                    if (
                        this.conversations[id].participants != null &&
                        this.conversations[id].participants.length > 0
                    ) {
                        for (const participant of this.conversations[id].participants) {
                            checked.push(participant.id);
                        }
                    }
                }
            }

            this.bulkContacts = { checked, closing: [], deleting: [], tagging: checked };
            await this.$store.dispatch('tagsLoad');

            this.$nextTick(() => {
                this.applyOrRemoveTagsStatus = true;
            });
        },

        async deleteAllConversation() {
            if (this.loading) return;

            this.loading = true;
            await this.$store.dispatch('deleteConversationsBatchClose', this.currentInboxId);
            await this.reset();
            this.loading = false;
        },

        async setUnreadConversation({ conversation }, type = 'default') {
            this.$store.dispatch('updateOrAddConversation', {
                conversation: conversation,
                updateOrder: true,
                updateProps: false,
                excludeProps: [
                    'draft_message',
                    'hasCurrentSocket',
                    'draftInitializedForm',
                    'debounceNotifyDrafting',
                ],
            });
            this.handleAddContact(conversation.contact_id, conversation.id);
        },

        async updateLastReadAt(conversation) {
            this.$store.dispatch('updateOrAddConversation', { conversation });
        },

        async messageSent(message) {
            if (!this.conversations[message.conversation_id]) return;

            await this.$store.dispatch('updateOrAddConversation', {
                conversation: {
                    id: message.conversation_id,
                    last_message_at: message.sent_at,
                    recent_message: {
                        id: message.id,
                        body: message.body,
                        status: message.status,
                        sent_at: message.sent_at,
                    },
                },
                updateProps: true,
                updateOrder: true,
            });

            this.markedRead(message.conversation_id);
        },

        async checkBlocking() {
            const id = this.conversation.contact.id;
            const blocked = this.conversation.contact.is_blocked;

            if (blocked) {
                this.$store.dispatch('contactsUnblock', { id }).then(() => {
                    this.contactBlocked(id);
                    this.$router.replace({
                        path: `/${this.route}`,
                        query: this.$route.query,
                    });
                });
            } else {
                this.blocking = this.conversation.contact;
            }
        },

        async started(conversation) {
            await this.$store.dispatch('updateEmptyConversation', { conversation });
            await this.handleAddContact(conversation.contact.id, conversation.id);
            await this.contactUpdated(conversation.contact);
            this.$store.dispatch('handleChangeStatus', false);

            setTimeout(() => {
                this.$router.push({
                    path: `/${this.route}/${conversation.id}`,
                    query: this.$route.query,
                });
            }, 500);
        },

        async createConversation(callback, form) {
            this.$refs.starter.enter(this.conversation.contact, callback, form);
        },

        async handleCloseDropDown() {
            if (this.$refs.starter) {
                this.$refs.starter.close();
            }
        },

        async handleChangeState([key, value]) {
            this[key] = value;
        },

        refreshList() {
            this.$refs.list.$refs.listCard.$children.forEach((v) => {
                v.$forceUpdate();
            });
        },

        loadIfLessThenLimit() {
            if (
                !this.closedAll &&
                !this.moreLoading &&
                Object.keys(this.conversations).length <= this.conversationsParameters.limit
            ) {
                this.loadMore();
            }
        },

        // read / unread
        async markedRead(id) {
            if (!this.conversations[id]) return;

            await this.$store.dispatch('updateOrAddConversation', {
                conversation: {
                    id: id,
                    last_read_at: this.conversations[id].last_message_at,
                },
                updateProps: true,
            });
        },

        async markedUnread(id) {
            if (!this.conversations[id]) return;

            await this.$store.dispatch('updateOrAddConversation', {
                conversation: {
                    id: id,
                    last_read_at: null,
                },
                updateProps: true,
            });
        },

        showConversationByFilter({ conversation, message }) {
            conversation = new Conversation(conversation);
            message = message ? new MessageSimple(message) : null;

            if (this.searchShowConversation && this.type !== 'extension') return;

            if (this.conversationsFilter === 'open') {
                if (message && message.isServerCommands) return true;

                return conversation.hasOpenFilter;
            }

            if (this.conversationsFilter === 'unread') {
                return true;
            }

            if (this.conversationsFilter === 'assigned') {
                return conversation.hasAssignedFilter;
            }

            if (this.conversationsFilter === 'unassigned') {
                return conversation.hasUnassignedFilter;
            }

            if (this.conversationsFilter === 'outbound') {
                return true;
            }

            if (this.conversationsFilter === 'pending') {
                return true;
            }

            if (this.conversationsFilter === 'closed') {
                return true;
            }

            if (this.conversationsFilter === 'optout') {
                return conversation.hasOptOutFilter;
            }

            if (this.conversationsFilter === 'failed') {
                return false;
            }

            if (this.conversationsFilter === 'blocked') {
                return conversation.hasBlockedFilter;
            }
        },

        // messages
        messageDeleted(e) {
            if (!this.conversation) return;
            if (e.conversation_id === this.conversation.id) {
                this.$store.dispatch('messageDeleted', e.id);
            }
        },

        getCorrectContact(id, participants) {
            return participants.find((item) => item.id === id);
        },

        async messageReceived(e) {
            const isCorrectContact = Boolean(Number(e.message.contact_id) === Number(e.contact.id));

            try {
                const conversation = e.conversation;
                const message = e.message;
                const contact = isCorrectContact
                    ? e.contact
                    : this.getCorrectContact(e.message.contact_id, e.conversation.participants);

                // TODO: SWR-14336: Will be uncommit and updated with SWR-14336
                // await this.$store.dispatch('setLogItem', {
                //     logLevel: 'info',
                //     message: 'EventMessageReceivedNewLog',
                //     details: {
                //         conversationId: conversation.id,
                //         messageId: message.id,
                //     },
                // });

                const inbox = await this.$store.dispatch('getTeamsById', {
                    id: conversation.inbox_id
                });
                const currentInbox = await this.$store.dispatch(
                    'getTeamsById',
                    {
                        id: this.currentInboxId
                    }
                );

                conversation.contact = contact;
                conversation.recent_message = message;
                conversation.has_new_message = true;
                message.contact = contact;

                const data = {
                    conversation: conversation,
                    message: message,
                };
                const isTranscribe = Boolean(
                    inbox.isTranscribeVoiceToText &&
                        message.type === 'call' &&
                        (message.status === 'record' ||
                            message.status === 'failed' ||
                            message.status === 'transcript')
                );

                this.loggerNewRelick(
                    'Conversation(messageReceived) - start',
                    message,
                    'warning',
                    1
                );

                if (!this.showConversationByFilter(data)) return;

                if (
                    this.currentInboxId !== null &&
                    this.currentInboxId !== conversation.inbox_id &&
                    currentInbox.type !== 'inbox_group' &&
                    this.isConversationView !== true
                )
                    return;
                if (
                    currentInbox.type === 'inbox_group' &&
                    !currentInbox.teamsIds.includes(conversation.inbox_id)
                )
                    return;

                // Skip the processing if the filter is closed.
                if (this.conversationsFilter === 'closed') return;

                // Skip if on the outbound filter, and the message is not in the list.
                if (this.conversationsFilter === 'outbound' && !this.conversations[conversation.id])
                    return;

                if (!isTranscribe) {
                    this.loggerNewRelick(
                        'Conversation(messageReceived) - start setUnreadConversation',
                        message,
                        'warning',
                        2
                    );
                    this.setUnreadConversation(data, 'received');
                    this.loggerNewRelick(
                        'Conversation(messageReceived) - end setUnreadConversation',
                        message,
                        'warning',
                        3
                    );
                }

                if (this.conversation && conversation.id === this.conversation.id) {
                    this.loggerNewRelick(
                        'Conversation(messageReceived) - start messageReceived',
                        message,
                        'warning',
                        4
                    );
                    this.$store.dispatch('messageReceived', message);
                    this.loggerNewRelick(
                        'Conversation(messageReceived) - end messageReceived',
                        message,
                        'warning',
                        5
                    );
                }
            } catch (e) {
                this.$log.error(e, this.$options.__file, 'messageReceived');
                this.loggerNewRelick(
                    'Conversation(messageReceived) - catch',
                    e.message,
                    'error',
                    1
                );
            }
        },

        messageTranscribeSuccess(e) {
            const eventMessage = e.message;

            this.$store.dispatch('getTranscribeMessage', eventMessage.id);
        },

        messageTranscribeFailed(e) {
            const eventMessage = e.message;

            this.$store.dispatch('getTranscribeMessage', eventMessage.id);
        },

        async messageUpdated(e) {
            try {
                const conversation = e.conversation;
                const message = e.message;
                const contact = e.contact;
                const tagging_reads = e.tagging_reads;
                const inbox = await this.$store.dispatch('getTeamsById', {
                    id: conversation.inbox_id
                });

                conversation.contact = contact;
                conversation.recent_message = message;
                conversation.hasNewMessage = true;
                message.contact = contact;

                const taggingAt = tagging_reads ? tagging_reads[this.user.id] : null;

                if (taggingAt) {
                    conversation.tagging_last_message_at = taggingAt.tagging_last_message_at;
                    conversation.tagging_last_read_at = taggingAt.tagging_last_read_at;
                }

                const data = {
                    conversation: conversation,
                    message: message,
                };
                const isTranscribe = Boolean(
                    inbox.isTranscribeVoiceToText &&
                        message.type === 'call' &&
                        (message.status === 'record' ||
                            message.status === 'failed' ||
                            message.status === 'transcript')
                );

                if (!this.showConversationByFilter(data)) return;

                this.loggerNewRelick(
                    'Conversation(messageUpdated) - start messageReceived',
                    message,
                    'warning',
                    1
                );

                if (
                    !isTranscribe &&
                    !(this.currentInboxId !== null && this.currentInboxId !== conversation.inbox_id)
                ) {
                    if (
                        this.conversationsFilter !== 'outbound' &&
                        (this.conversationsFilter === 'assigned' ||
                            conversation.started_at ||
                            conversation.closed_at)
                    ) {
                        this.loggerNewRelick(
                            'Conversation(messageUpdated) - start setUnreadConversation',
                            e,
                            'warning',
                            2
                        );
                        this.setUnreadConversation(data, 'updated');
                        this.loggerNewRelick(
                            'Conversation(messageUpdated) - end setUnreadConversation',
                            e,
                            'warning',
                            3
                        );
                    } else if (
                        this.conversationsFilter === 'outbound' &&
                        !conversation.started_at &&
                        !conversation.closed_at
                    ) {
                        this.loggerNewRelick(
                            'Conversation(messageUpdated) - start setUnreadConversation',
                            e,
                            'warning',
                            6
                        );
                        this.setUnreadConversation(data, 'updated');
                        this.loggerNewRelick(
                            'Conversation(messageUpdated) - end setUnreadConversation',
                            e,
                            'warning',
                            7
                        );
                    }
                }

                if (this.conversation && this.conversation.id === message.conversation_id) {
                    this.loggerNewRelick(
                        'Conversation(messageUpdated) - start messageUpdated',
                        message,
                        'warning',
                        8
                    );
                    this.$store.dispatch('messageUpdated', message);
                    this.loggerNewRelick(
                        'Conversation(messageUpdated) - end messageUpdated',
                        message,
                        'warning',
                        9
                    );
                }
            } catch (e) {
                this.$log.error(e, this.$options.__file, 'messageUpdated');
                this.loggerNewRelick('Conversation(messageUpdated) - catch', e.message, 'error', 1);
            }
        },

        loggerNewRelick(name, e, type, index) {
            this.$debugNR(
                name,
                e,
                type,
                {
                    conversation: {
                        id: this.conversation && this.conversation.id,
                    },
                    message: {
                        id: e.id,
                        body: e.body,
                        conversationId: e.conversationId,
                        created_at: e.created_at,
                        sent_at: e.sent_at,
                        userId: e.user && e.user.id,
                        contactId: e.contact && e.contact.id,
                    },
                },
                index
            );
        },

        eventConversationOpened(conversation) {
            const data = {
                conversation: conversation,
            };

            if (!this.showConversationByFilter(data)) return;
            if (this.currentInboxId !== null && this.currentInboxId !== conversation.inbox_id)
                return;

            this.$store.dispatch('updateOrAddConversation', {
                conversation: conversation,
            });
            this.handleAddContact(conversation.contact_id, conversation.id);
        },

        eventConversationClosed(conversation) {
            this.updateAndRemoveConversation(conversation);
        },

        eventConversationContactBlocked(conversation) {
            if (this.conversationsFilter === 'blocked') {
                this.$store.dispatch('updateOrAddConversation', {
                    conversation: conversation,
                });
                this.handleAddContact(conversation.contact_id, conversation.id);
            } else {
                this.contactBlocked(conversation.contact_id);
            }
        },

        eventConversationContactOptedIn(conversation) {
            this.$store.dispatch('updateConversation', {
                conversation: conversation,
            });
        },

        eventConversationContactOptedOut(conversation) {
            this.$store.dispatch('updateConversation', {
                conversation: conversation,
            });
        },

        // typing
        typing() {
            if (!this.conversation) return;

            let channel = '';
            const inboxId =
                this.currentInboxId === null ? this.conversation.inbox_id : this.currentInboxId;

            if (inboxId) {
                channel = `inbox.${inboxId}.conversation`;
            } else {
                channel = `user.${this.user.id}.conversation`;
            }

            const privateChannel = this.$echo.channel(`private-${channel}`);

            if (privateChannel && privateChannel.whisper) {
                privateChannel.whisper('typing', {
                    id: this.user.id,
                    first_name: this.user.firstName,
                    last_name: this.user.lastName,
                    conversation_id: this.conversation.id,
                });
            }
        },

        clientTyping(e) {
            if (this.conversation && e.conversation_id === this.conversation.id) {
                this.$store.dispatch('userTypingAdd', e);
                this.$store.dispatch('userTypingRemove', e);
            }
        },

        // draft events
        handleClientDraft(e) {
            const conversation = this.conversations && this.conversations[e.conversation_id];

            if (conversation) {
                this.$store.dispatch('updateOrAddConversation', {
                    conversation: {
                        id: conversation.id,
                        draft_message: {
                            message: e.message,
                        },
                        hasCurrentSocket: e.socketId === this.$echo.socketId(),
                    },
                    updateOrder: false,
                    updateProps: true,
                });
            }
        },

        messageDeletedDraft(e) {
            this.$store.dispatch('deleteMessagesDraft', {
                isAjax: false,
                id: e.conversation.id,
            });
        },

        // contacts
        handleAddContact(contactId, conversationId) {
            if (Array.isArray(this.contacts[contactId])) {
                this.contacts[contactId].push(conversationId);
            } else {
                this.contacts[contactId] = [];
                this.contacts[contactId].push(conversationId);
            }
        },

        contactUpdated(contact) {
            if (!(contact instanceof Contact)) {
                contact = new Contact(contact);
            }

            this.$store.dispatch('updateContactByConversation', {
                id: this.conversation?.id,
                contact: contact,
            });
        },

        contactDeleted(id) {
            if (this.contacts[id]) {
                this.contacts[id].forEach(async (conversationId) => {
                    if (!conversationId) return;

                    if (parseInt(this.$route.params.id) === conversationId) {
                        this.$router.replace({
                            path: `/${this.route}`,
                            query: this.$route.query,
                        });
                    }

                    await this.$store.dispatch('deleteConversation', conversationId);

                    if (this.contacts[id]) {
                        this.contacts[id] = this.contacts[id].filter((id) => id !== conversationId);

                        if (!this.contacts[id].length) {
                            delete this.contacts[id];
                        }
                    }
                });
            }
        },

        contactBlocked(id) {
            if (this.contacts[id]) {
                this.contacts[id].forEach(async (conversationId) => {
                    if (!conversationId) return;

                    await this.$store.dispatch('updateOrder', {
                        id: conversationId,
                        type: 'delete',
                    });
                });
            }
        },

        contactsDeleted(ids) {
            ids.forEach((id) => {
                this.contactDeleted(id);
            });
        },

        // Change Inbox
        async inboxChanged(teamTypeId, teamType, isRedirect = true) {
            await this.echoDisconnect();
            await this.reset();

            await this.$store.dispatch('activateInbox', teamTypeId);
            await this.setInbox(teamTypeId, teamType);

            if (isRedirect && this.$route.params.id) {
                this.$router.push({
                    path: `/${this.route}`,
                    query: this.$route.query,
                });
            }

            await this.throttleLoad(undefined, teamTypeId, teamType, false);
        },

        async setInbox(id, type) {
            const inbox = await this.$store.dispatch('updateUsersTeam', id);
            await this.$store.dispatch('setUnreadCount', inbox);
        },

        // modals
        handleChangeModalChooseParticipants(status) {
            this.showModalChooseParticipants = status;
        },

        handleChangeModalReassign(status) {
            this.showModalReassign = status;
        },

        handleChangeModalCall(status) {
            this.showModalCall = status;
        },

        handleDeleteConversation() {
            this.deleting = this.conversation.contact;
        },

        handleChangeModalPreview(item = false) {
            this.selectedItemMedia = item;
        },

        openCancellingMessageModal(status = true, item = null) {
            this.showCancellingMessage = status;
            this.showCancellingMessageItem = item;
        },

        // echo server (web socket)
        async bulkConversationsClosed(data) {
            this.loadIfLessThenLimit();
        },

        presenceChannel() {
            this.inboxesIds.forEach((id) => {
                let channel = '';

                if (id) {
                    channel = `inbox.${id}.conversation`;
                } else {
                    channel = `user.${this.user.id}.conversation`;
                }

                this.$echo
                    .join(channel)
                    .listen('.conversation.closed', this.eventConversationClosed)
                    .listen('.conversation.opened', this.eventConversationOpened)
                    .listen('.conversation.contact.blocked', this.eventConversationContactBlocked)
                    .listen('.conversation.contact.opted-in', this.eventConversationContactOptedIn)
                    .listen(
                        '.conversation.contact.opted-out',
                        this.eventConversationContactOptedOut
                    )
                    .listen('.conversation.reassigned', this.conversationUpdated)
                    .listen('.message.deleted', this.messageDeleted)
                    .listen('.message.received.new', this.messageReceived)
                    .listen('.voicemail.received', this.messageReceived)
                    .listen('.message.updated.new', this.messageUpdated)
                    .listen('.client-typing', this.clientTyping)
                    .listen('.conversations.bulk.closed', this.bulkConversationsClosed)
                    .listen('.conference.participant_join', this.eventConferenceParticipantUpdate)
                    .listen('.conference.participant_leave', this.eventConferenceParticipantUpdate);
            });
        },

        privateChannel() {
            this.$echo
                .private(`user.${this.user.id}`)
                .listen('.client-draft', this.handleClientDraft)
                .listen('.message.deleted.draft', this.messageDeletedDraft);

            this.inboxesIds.forEach((id) => {
                let channel = '';

                if (id) {
                    channel = `inbox.${id}.conversation`;
                } else {
                    channel = `user.${this.user.id}.conversation`;
                }

                this.$echo
                    .private(channel)
                    .listen('.conversation.closed', this.eventConversationClosed)
                    .listen('.conversation.opened', this.eventConversationOpened)
                    .listen('.conversation.contact.blocked', this.eventConversationContactBlocked)
                    .listen('.conversation.contact.opted-in', this.eventConversationContactOptedIn)
                    .listen(
                        '.conversation.contact.opted-out',
                        this.eventConversationContactOptedOut
                    )
                    .listen('.conversation.reassigned', this.conversationUpdated)
                    .listen('.message.deleted', this.messageDeleted)
                    .listen('.message.received.new', this.messageReceived)
                    .listen('.voicemail.received', this.messageReceived)
                    .listen('.message.updated.new', this.messageUpdated)
                    .listen('.client-typing', this.clientTyping)
                    .listen('.conversations.bulk.closed', this.bulkConversationsClosed)
                    .listen('.conference.participant_join', this.eventConferenceParticipantUpdate)
                    .listen('.conference.participant_leave', this.eventConferenceParticipantUpdate)
                    .listen('.message.transcript.completed', this.messageTranscribeSuccess)
                    .listen('.message.transcript.failed', this.messageTranscribeFailed);
            });
        },

        stopListeningEventsChannel() {
            this.$echo
                .private(`user.${this.user.id}`)
                .stopListening('.client-draft')
                .stopListening('.message.deleted.draft');

            this.inboxesIds.forEach((id) => {
                let channel = '';

                if (id) {
                    channel = `inbox.${id}.conversation`;
                } else {
                    channel = `user.${this.user.id}.conversation`;
                }

                this.$echo
                    .private(channel)
                    .stopListening('.conversation.closed')
                    .stopListening('.conversation.opened')
                    .stopListening('.conversation.contact.blocked')
                    .stopListening('.conversation.contact.opted-in')
                    .stopListening('.conversation.contact.opted-out')
                    .stopListening('.conversation.reassigned')
                    .stopListening('.message.deleted')
                    .stopListening('.message.received.new')
                    .stopListening('.voicemail.received')
                    .stopListening('.message.updated.new')
                    .stopListening('.client-typing')
                    .stopListening('.conversations.bulk.closed')
                    .stopListening('.conference.participant_join')
                    .stopListening('.conference.participant_leave')
                    .stopListening('.message.transcript.completed')
                    .stopListening('.message.transcript.failed');

                this.$echo
                    .join(channel)
                    .stopListening('.conversation.closed')
                    .stopListening('.conversation.opened')
                    .stopListening('.conversation.contact.blocked')
                    .stopListening('.conversation.contact.opted-in')
                    .stopListening('.conversation.contact.opted-out')
                    .stopListening('.conversation.reassigned')
                    .stopListening('.message.deleted')
                    .stopListening('.message.received.new')
                    .stopListening('.voicemail.received')
                    .stopListening('.message.updated.new')
                    .stopListening('.client-typing')
                    .stopListening('.conversations.bulk.closed')
                    .stopListening('.conference.participant_join')
                    .stopListening('.conference.participant_leave')
                    .stopListening('.message.transcript.completed')
                    .stopListening('.message.transcript.failed');
            });
        },

        async handleSaveActiveConversation() {
            try {
                if (!this.routeId) return;

                const conversation = await this.$store.dispatch('getConversationsById', {
                    id: this.routeId,
                });

                await this.$store.dispatch('initConversation', conversation);
            } catch (e) {
                this.handleShowModalCheckConversation(true);

                this.$log.error(e, this.$options.__file, 'handleSaveActiveConversation');
            }
        },

        async handleToggleInboxForConversation() {
            try {
                if (!this.routeId) return;
                if (!this.isConversationIdEqualInboxId) {
                    await this.$store.dispatch('activateInbox', this.conversation.inbox_id);
                    await this.inboxChanged(
                        this.conversation.inbox_id,
                        this.conversation.userOwner.currentInboxType,
                        false
                    );
                }
            } catch (e) {
                this.$log.error(e, this.$options.__file, 'handleToggleInboxForConversation');
            }
        },

        handleIntegrationSlideShowFinish() {
            this.integrationSlideShowFinish = this.integrationSlideShow;
        },

        handleIntegrationSlideShow() {
            this.integrationSlideShow = !this.integrationSlideShow;

            if (this.integrationSlideShow === true) this.integrationSlideShowFinish = true;

            this.debounceHandleIntegrationSlideShowFinish();
        },

        handleShowModalEditScheduled(status = false, item = null) {
            this.showModalEditScheduled = status;
            this.showModalEditScheduledItem = item;
        },

        handleShowModalCheckConversation(status = false) {
            this.showModalCheckConversation = status;
        },

        async handleOpenConversation(event, id) {
            event.preventDefault();

            this.$rootEvents.$emit('filterChanged', 'open', async () => {
                this.route = 'conversations';
                await this.$router.push({
                    path: `/conversations/${id}`,
                });
                this.load();
            });
        },

        handleOpenConversationContactView(event, id) {
            event.preventDefault();

            this.$rootEvents.$emit('conversationSelect', this.conversations[id]);
        },

        // Conference Calling
        eventConferenceParticipantUpdate(data) {
            this.$store.dispatch('updateConferenceParticipant', {
                data: data,
            });
        },
    },

    created() {
        this.openConversation = this.handleOpenConversation.bind(this);
        this.openConversationContactView = this.handleOpenConversationContactView.bind(this);
    },

    mounted() {
        this.debounceHandleIntegrationSlideShowFinish = debounce(
            this.handleIntegrationSlideShowFinish,
            500
        );
        this.throttleLoad = throttle(this.load, 500, { trailing: false });
    },
};
